/**
 * キャンペーン一覧。
 * 仕様書: docs/01_spec/spec_line_bot.md A-11.5
 *
 * Phase 23a-ext-3:
 *   - 「キャンペーン」と「テンプレート」をタブ分離
 *   - 「+ 新規キャンペーン」→ TemplatePickerDialog（白紙 / 組込テンプレート / 自作テンプレートから選択）
 */
import { Bookmark, Copy, FileBox, Plus, Sparkles, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useCampaigns,
  useCreateCampaign,
  useDeleteCampaign,
  type CampaignRecord,
} from '@/hooks/useCampaigns';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/firestoreTime';

import { TemplatePickerDialog } from './TemplatePickerDialog';

type TabKey = 'campaigns' | 'templates';

interface CampaignsListPageProps {
  /**
   * v3.3: Hub に組み込む際に内部タブを隠し、表示モードを外部から固定する。
   * 指定なし = 従来の独立画面 (内部タブ表示)。
   */
  forcedView?: TabKey;
}

export function CampaignsListPage({ forcedView }: CampaignsListPageProps = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';
  const { data, isLoading } = useCampaigns();
  const deleteMutation = useDeleteCampaign();
  const createMutation = useCreateCampaign();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [internalTab, setInternalTab] = useState<TabKey>('campaigns');
  const tab: TabKey = forcedView ?? internalTab;
  const setTab = (next: TabKey) => {
    if (!forcedView) setInternalTab(next);
  };

  const { campaigns, templates } = useMemo(() => {
    const list = data ?? [];
    return {
      campaigns: list.filter((c) => !c.isTemplate),
      templates: list.filter((c) => !!c.isTemplate),
    };
  }, [data]);

  const visible = tab === 'campaigns' ? campaigns : templates;

  const handleDelete = async (c: CampaignRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    if (
      !window.confirm(
        c.isTemplate
          ? `テンプレート「${c.name}」を削除します。`
          : `キャンペーン「${c.name}」を削除します。各リソースの campaignIds から自動的に除去されます。`,
      )
    )
      return;
    try {
      await deleteMutation.mutateAsync(c.id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(
        `削除失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  /**
   * キャンペーン複製。
   * 構造（elements / connections / 詳細）はコピー、メタデータは初期化する：
   *   - id は新規採番
   *   - name に "(コピー)" suffix
   *   - paused は true（複製先は計画中スタート）
   *   - status は 'planning' に戻す
   *   - イベントカードの lastFiredAt はクリア（複製先で再発火可能に）
   *   - 集計系（submissionCount 等）は対象外（Campaign に存在しない）
   *
   * 注：refId は維持（既存リソースを共有）。意図しないなら admin が複製後に手動修正。
   */
  const handleDuplicate = async (c: CampaignRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    setDuplicatingId(c.id);
    try {
      const elements = (c.elements ?? []).map((el) => {
        if (el.type === 'event') {
          const { lastEventStartFiredAt, lastEventEndFiredAt, ...rest } = el;
          // 利用しない変数の警告回避
          void lastEventStartFiredAt;
          void lastEventEndFiredAt;
          return rest;
        }
        return el;
      });
      const newId = await createMutation.mutateAsync({
        name: `${c.name}(コピー)`,
        status: 'planning',
        elements,
        connections: c.connections ?? [],
        bounds: c.bounds,
        startAt: null,
        endAt: null,
        background_info: c.background_info,
        background_info_schema: c.background_info_schema,
        isTemplate: c.isTemplate,
        // 複製は必ず休止（計画中）から開始
        paused: true,
        createdBy: user?.uid ?? '',
      });
      toast.success(c.isTemplate ? 'テンプレートを複製しました' : 'キャンペーンを複製しました');
      // 編集画面に遷移して即時調整できるようにする
      navigate(`/campaigns/${newId}`);
    } catch (err) {
      toast.error(
        `複製失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setDuplicatingId(null);
    }
  };

  const tabClass = (active: boolean) =>
    cn(
      'px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors inline-flex items-center gap-2 cursor-pointer',
      active
        ? 'text-[var(--accent)] border-[var(--accent)]'
        : 'text-[var(--text-muted)] border-transparent hover:text-[var(--text-primary)]',
    );

  const actionsNode = canEdit ? (
    <Button
      type="button"
      variant="primary"
      onClick={() => setPickerOpen(true)}
    >
      <Plus />
      新規キャンペーン
    </Button>
  ) : null;

  // v3.3 IA: Hub 組み込み時 (forcedView) はタイトルを内容に合わせて切替
  const screenTitle =
    forcedView === 'templates'
      ? 'ファネル雛形'
      : forcedView === 'campaigns'
        ? 'ファネル一覧'
        : 'キャンペーン';
  const screenDescription =
    forcedView === 'templates'
      ? '保存したファネル雛形から新規キャンペーンを起動できます'
      : forcedView === 'campaigns'
        ? '実行中・計画中のキャンペーンファネル'
        : 'マルチチャネル施策をホワイトボード型 UI で設計・実行';

  const body = (
    <>
      {/* タブ — Hub 組み込み時 (forcedView 指定時) は非表示 */}
      {!forcedView && (
        <div className="flex gap-1 border-b border-[var(--border)] mb-5">
          <button
            type="button"
            className={tabClass(tab === 'campaigns')}
            onClick={() => setTab('campaigns')}
          >
            <Sparkles className="size-3.5" />
            キャンペーン
            <Badge variant={tab === 'campaigns' ? 'accent' : 'default'}>
              {campaigns.length}
            </Badge>
          </button>
          <button
            type="button"
            className={tabClass(tab === 'templates')}
            onClick={() => setTab('templates')}
          >
            <Bookmark className="size-3.5" />
            自作テンプレート
            <Badge variant={tab === 'templates' ? 'accent' : 'default'}>
              {templates.length}
            </Badge>
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <Spinner />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={tab === 'campaigns' ? Sparkles : FileBox}
          title={
            tab === 'campaigns'
              ? 'キャンペーンがまだありません'
              : '自作テンプレートはまだありません'
          }
          description={
            tab === 'campaigns'
              ? '「新規キャンペーン」から組込テンプレートを起点に作成できます'
              : 'キャンペーンボードの「テンプレートとして保存」ボタンで、作成したフローをテンプレート化できます'
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => navigate(`/campaigns/${c.id}`)}
              className="text-left rounded bg-[var(--bg-surface)] border border-[var(--border)] hover:border-[var(--accent)] transition-colors p-4 group"
            >
              <div className="flex items-start gap-2 mb-2">
                {c.isTemplate ? (
                  <Bookmark className="size-5 text-[var(--accent-cyan)] flex-shrink-0 mt-0.5" />
                ) : (
                  <Sparkles className="size-5 text-[var(--accent-purple)] flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-semibold text-sm truncate">{c.name}</h3>
                    {c.isTemplate ? (
                      <Badge variant="accent">テンプレート</Badge>
                    ) : c.paused === true ? (
                      <Badge variant="draft">計画中</Badge>
                    ) : (
                      <Badge variant="approved">稼働中</Badge>
                    )}
                  </div>
                  {(() => {
                    // background_info.description を優先、無ければ旧 description にフォールバック
                    const desc =
                      (typeof c.background_info?.description === 'string'
                        ? c.background_info.description
                        : '') || c.description || '';
                    if (!desc) return null;
                    return (
                      <p className="text-xs text-[var(--text-secondary)] line-clamp-2 leading-relaxed">
                        {desc}
                      </p>
                    );
                  })()}
                </div>
              </div>
              <div className="text-xs text-[var(--text-muted)] flex items-center gap-3 mb-3 flex-wrap">
                <span>{c.elements?.length ?? 0} カード</span>
                <span>{c.connections?.length ?? 0} 接続</span>
                <span>更新 {formatDate(c.updatedAt)}</span>
              </div>
              {canEdit && (
                <div className="flex justify-end gap-1 pt-3 border-t border-[var(--border)] opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => void handleDuplicate(c, e)}
                    disabled={duplicatingId === c.id || createMutation.isPending}
                    title="複製（同じ構成で新しいキャンペーンを作成）"
                  >
                    {duplicatingId === c.id ? (
                      <Spinner className="size-3.5" />
                    ) : (
                      <Copy className="size-3.5" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => void handleDelete(c, e)}
                    title="削除"
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      <TemplatePickerDialog open={pickerOpen} onOpenChange={setPickerOpen} />
    </>
  );

  // v3.3 IA: embedded 時も Screen ラッパで描画し、他の Hub タブ
  // (ランディング・フォーム等) と同じ screen-body padding を維持。
  return (
    <Screen
      title={screenTitle}
      description={screenDescription}
      actions={actionsNode ?? undefined}
    >
      {body}
    </Screen>
  );
}
